import { useAuth, useCurrency, useCleviaGuide } from '@/contexts';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Wallet,
  LogOut,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  BarChart3,
  BookOpen,
} from 'lucide-react';
import WelcomeModal from '@/components/WelcomeModal';
import { TransferModal } from '@/components/TransferModal';
import { RecurringTransactionModal } from '@/components/RecurringTransactionModal';
import { useAccounts, ACCOUNT_GROUPS } from '@/hooks/useAccounts';
import { useConvertedNetWorth } from '@/hooks/useConvertedNetWorth';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import {
  NET_WORTH_PERIOD_DAYS,
  useBalanceChart,
  type NetWorthPeriod,
} from '@/hooks/useBalanceChart';
import { useTransactions } from '@/hooks/useTransactions';
import { useMultipleAccountSparklines } from '@/hooks/useAccountSparkline';
import {
  useRecurringTransactions,
  type RecurringTransaction,
} from '@/hooks/useRecurringTransactions';
import { useTranslation } from '@/hooks/useTranslation';

import { useAchievementTriggers } from '@/hooks/useAchievementTriggers';
import { AchievementsDropdown } from '@/components/AchievementsDropdown';

import { supabase } from '@/lib/supabase';

// Import new dashboard components
import {
  NetWorthBlock,
  AccountCards,
  PatrimoineDistribution,
  BudgetBlock,
  TopCategories,
  CashFlowChart,
  RecentTransactions,
  UpcomingBills,
  GoalsPreview,
} from '@/components/dashboard';

interface Goal {
  id: string;
  presetId: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  targetDate: string | null;
  linkedAccountId: string | null;
  priority: number;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
}

export default function DashboardPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const { user, logout } = useAuth();
  const { openGuide } = useCleviaGuide();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { formatAmount, formatAmountInCurrency, currency } = useCurrency();
  const { getRate } = useExchangeRates();
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<NetWorthPeriod>('1m');
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState<RecurringTransaction | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return (
      selectedMonth.getFullYear() === now.getFullYear() &&
      selectedMonth.getMonth() === now.getMonth()
    );
  }, [selectedMonth]);

  const navigateMonth = useCallback((direction: -1 | 1) => {
    setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
  }, []);

  const goToCurrentMonth = useCallback(() => {
    const now = new Date();
    setSelectedMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const selectedMonthLabel = useMemo(() => {
    return selectedMonth.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
  }, [selectedMonth, language]);
  const [accountFilter, setAccountFilter] = useState<string[]>([]);
  const [isAccountFilterOpen, setIsAccountFilterOpen] = useState(false);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [patrimoineExpanded, setPatrimoineExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('clevia_patrimoine_expanded');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const togglePatrimoine = useCallback(() => {
    setPatrimoineExpanded((prev: boolean) => {
      const next = !prev;
      localStorage.setItem('clevia_patrimoine_expanded', JSON.stringify(next));
      return next;
    });
  }, []);

  const [budgetExpanded, setBudgetExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('clevia_budget_expanded');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleBudget = useCallback(() => {
    setBudgetExpanded((prev: boolean) => {
      const next = !prev;
      localStorage.setItem('clevia_budget_expanded', JSON.stringify(next));
      return next;
    });
  }, []);

  // Fetch real data from hooks
  const {
    accounts,
    loadAccounts,
    calculateStats: calculateAccountStats,
    loading: accountsLoading,
  } = useAccounts();
  const { transactions, loading: transactionsLoading, loadTransactions } = useTransactions();
  const {
    getUpcoming,
    createRecurringTransaction,
    updateRecurringTransaction,
    loadRecurringTransactions,
    loading: recurringLoading,
  } = useRecurringTransactions();

  // Achievement triggers based on data
  useAchievementTriggers({
    transactionCount: transactions.length,
    accountCount: accounts.length,
    recurringCount: getUpcoming?.length ?? 0,
    goalCount: goals.length,
    hasIncome: transactions.some((t) => t.type === 'income'),
    hasExpense: transactions.some((t) => t.type === 'expense'),
    hasTransfer: transactions.some((t) => t.type === 'transfer'),
    categorizedExpenseCount: transactions.filter((t) => t.type === 'expense' && t.category_id)
      .length,
  });

  // Handle save (create or update)
  const handleSaveRecurring = async (data: any) => {
    if (editingRecurring) {
      return updateRecurringTransaction(editingRecurring.id, data);
    }
    return createRecurringTransaction(data);
  };

  // Calculate real KPIs
  const accountStats = useMemo(() => calculateAccountStats(), [accounts, calculateAccountStats]);
  const { data: convertedNetWorth } = useConvertedNetWorth();

  const visibleAccounts = useMemo(
    () => accounts.filter((account) => !account.is_hidden),
    [accounts]
  );

  const allVisibleAccountIds = useMemo(
    () => visibleAccounts.map((account) => account.id),
    [visibleAccounts]
  );

  const selectedAccountIds = useMemo(
    () => accountFilter.filter((id) => allVisibleAccountIds.includes(id)),
    [accountFilter, allVisibleAccountIds]
  );

  const isAllAccountsSelected = selectedAccountIds.length === 0;

  const effectiveAccountIds = useMemo(
    () => (isAllAccountsSelected ? allVisibleAccountIds : selectedAccountIds),
    [allVisibleAccountIds, isAllAccountsSelected, selectedAccountIds]
  );

  const filteredAccounts = useMemo(
    () => visibleAccounts.filter((account) => effectiveAccountIds.includes(account.id)),
    [visibleAccounts, effectiveAccountIds]
  );

  const groupedFilterAccounts = useMemo(() => {
    const allGroups = [
      ...ACCOUNT_GROUPS.asset.map((group) => ({ ...group, type: 'asset' as const })),
      ...ACCOUNT_GROUPS.liability.map((group) => ({ ...group, type: 'liability' as const })),
    ];

    return allGroups
      .map((group) => ({
        key: group.key,
        label: t(`accounts.group.${group.key}` as any),
        accounts: visibleAccounts.filter(
          (account) =>
            account.type === group.type && group.subtypes.includes(account.subtype as never)
        ),
      }))
      .filter((group) => group.accounts.length > 0);
  }, [t, visibleAccounts]);

  const accountFilterLabel = useMemo(() => {
    if (isAllAccountsSelected) return t('dashboard.filter.allAccounts' as any);
    if (filteredAccounts.length === 1)
      return filteredAccounts[0]?.name || t('dashboard.filter.allAccounts' as any);
    return `${filteredAccounts.length} ${t('dashboard.accounts')}`;
  }, [filteredAccounts, isAllAccountsSelected, t]);

  const toggleAccountSelection = (accountId: string) => {
    setAccountFilter((previous) => {
      const current = previous.filter((id) => allVisibleAccountIds.includes(id));

      if (current.includes(accountId)) {
        const next = current.filter((id) => id !== accountId);
        return next.length === 0 ? [] : next;
      }

      if (current.length === 0) {
        return [accountId];
      }

      return [...current, accountId];
    });
  };

  const removeAccountSelection = (accountId: string) => {
    setAccountFilter((previous) => {
      const current = previous.filter((id) => allVisibleAccountIds.includes(id));
      if (!current.includes(accountId)) return current;

      const next = current.filter((id) => id !== accountId);
      return next.length === 0 ? [] : next;
    });
  };

  const clearAccountFilter = () => {
    setAccountFilter([]);
  };

  // Filtered net worth based on account filter
  const filteredNetWorth = useMemo(() => {
    if (isAllAccountsSelected) {
      return convertedNetWorth ? convertedNetWorth.net_worth : accountStats.netWorth;
    }

    return filteredAccounts.reduce((sum, account) => {
      const acctCurrency = (account as any).currency || 'EUR';
      const rate = getRate(acctCurrency, currency) ?? 1;
      const converted = account.balance * rate;
      if (account.type === 'liability') {
        return sum - Math.abs(converted);
      }
      return sum + converted;
    }, 0);
  }, [
    accountStats.netWorth,
    filteredAccounts,
    isAllAccountsSelected,
    convertedNetWorth,
    getRate,
    currency,
  ]);

  // Get net worth history based on selected period
  const accountCurrencyMap = useMemo(
    () => new Map(filteredAccounts.map((a) => [a.id, (a as any).currency || 'EUR'])),
    [filteredAccounts]
  );
  const convertBalance = useCallback(
    (balance: number, accountId: string) => {
      const acctCurrency = accountCurrencyMap.get(accountId) || 'EUR';
      const rate = getRate(acctCurrency, currency) ?? 1;
      return balance * rate;
    },
    [accountCurrencyMap, getRate, currency]
  );
  const { data: netWorthHistory } = useBalanceChart(
    filteredAccounts,
    selectedPeriod === 'all' ? 'all' : NET_WORTH_PERIOD_DAYS[selectedPeriod],
    undefined,
    convertBalance
  );

  // Get sparkline data for accounts
  const { data: sparklineData, loading: sparklineLoading } =
    useMultipleAccountSparklines(filteredAccounts);

  // Load goals from user metadata
  useEffect(() => {
    const loadGoals = async () => {
      if (!user) return;
      try {
        const {
          data: { user: userData },
        } = await supabase.auth.getUser();
        const savedGoals = userData?.user_metadata?.goals || [];
        setGoals(savedGoals.filter((g: Goal) => g.status === 'active'));
      } catch (error) {
        console.error('Error loading goals:', error);
      }
    };
    loadGoals();
  }, [user]);

  // Get date range based on period filter
  const dateRange = useMemo(() => {
    const startDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
    const now = new Date();
    // End of selected month, or now if current month
    const endOfMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
      23,
      59,
      59
    );
    const endDate = isCurrentMonth ? now : endOfMonth;
    return { startDate, endDate };
  }, [selectedMonth, isCurrentMonth]);

  // Filter transactions by period and account (exclude display-only recurring)
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (t.is_recurring) return false;
      const date = new Date(t.date);
      const inRange = date >= dateRange.startDate && date <= dateRange.endDate;
      const inAccount =
        isAllAccountsSelected ||
        (t.account_id ? effectiveAccountIds.includes(t.account_id) : false) ||
        (t.to_account_id ? effectiveAccountIds.includes(t.to_account_id) : false);
      return inRange && inAccount;
    });
  }, [transactions, dateRange, effectiveAccountIds, isAllAccountsSelected]);

  // Get comparison period transactions (previous equivalent period)
  const comparisonTransactions = useMemo(() => {
    const duration = dateRange.endDate.getTime() - dateRange.startDate.getTime();
    const compStart = new Date(dateRange.startDate.getTime() - duration);
    const compEnd = new Date(dateRange.startDate.getTime() - 1);

    return transactions.filter((t) => {
      if (t.is_recurring) return false;
      const date = new Date(t.date);
      const inRange = date >= compStart && date <= compEnd;
      const inAccount =
        isAllAccountsSelected ||
        (t.account_id ? effectiveAccountIds.includes(t.account_id) : false) ||
        (t.to_account_id ? effectiveAccountIds.includes(t.to_account_id) : false);
      return inRange && inAccount;
    });
  }, [transactions, dateRange, effectiveAccountIds, isAllAccountsSelected]);

  // Calculate filtered income/expense
  const filteredIncome = useMemo(
    () =>
      filteredTransactions
        .filter((t) => t.type === 'income')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0),
    [filteredTransactions, currency, getRate]
  );
  const filteredExpense = useMemo(
    () =>
      filteredTransactions
        .filter((t) => t.type === 'expense')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0),
    [filteredTransactions, currency, getRate]
  );

  // Calculate comparison income/expense
  const comparisonIncome = useMemo(
    () =>
      comparisonTransactions
        .filter((t) => t.type === 'income')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0),
    [comparisonTransactions, currency, getRate]
  );
  const comparisonExpense = useMemo(
    () =>
      comparisonTransactions
        .filter((t) => t.type === 'expense')
        .reduce((sum, t) => {
          const acctCurrency = (t as any).account?.currency || 'EUR';
          const rate = getRate(acctCurrency, currency) ?? 1;
          return sum + t.amount * rate;
        }, 0),
    [comparisonTransactions, currency, getRate]
  );

  // Get upcoming recurring transactions (next 60 days)
  const upcomingRecurring = useMemo(() => {
    const all = getUpcoming(60);
    if (isAllAccountsSelected) return all;
    return all.filter(
      (item) => item.recurring.account_id && effectiveAccountIds.includes(item.recurring.account_id)
    );
  }, [effectiveAccountIds, getUpcoming, isAllAccountsSelected]);

  // Update goals with linked account balances
  const goalsWithProgress = useMemo(() => {
    return goals
      .filter((goal) => {
        if (isAllAccountsSelected) return true;
        return !goal.linkedAccountId || effectiveAccountIds.includes(goal.linkedAccountId);
      })
      .map((goal) => {
        let currentAmount = goal.currentAmount;
        if (goal.linkedAccountId) {
          const linkedAccount = accounts.find((a) => a.id === goal.linkedAccountId);
          if (linkedAccount) {
            currentAmount = linkedAccount.balance;
          }
        }
        const progress =
          goal.targetAmount > 0 ? Math.min((currentAmount / goal.targetAmount) * 100, 100) : 0;
        const daysLeft = goal.targetDate
          ? Math.max(
              0,
              Math.ceil((new Date(goal.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            )
          : null;
        return { ...goal, currentAmount, progress, daysLeft };
      })
      .slice(0, 3);
  }, [goals, accounts, effectiveAccountIds, isAllAccountsSelected]);

  // Load user avatar from Supabase metadata
  useEffect(() => {
    const loadAvatar = async () => {
      const {
        data: { user: supaUser },
      } = await supabase.auth.getUser();
      if (supaUser?.user_metadata?.avatar_url) {
        setAvatarUrl(supaUser.user_metadata.avatar_url as string);
      }
    };
    loadAvatar();
  }, []);

  useEffect(() => {
    const shouldShowWelcome = localStorage.getItem('showWelcome');
    const userName = localStorage.getItem('userName');

    if (shouldShowWelcome === 'true' && userName) {
      setWelcomeName(userName);
      setShowWelcome(true);
      localStorage.removeItem('showWelcome');
      localStorage.removeItem('userName');
    }
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isLoading = accountsLoading || transactionsLoading;

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="dashboard" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        {/* Page Header */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700/50 sticky top-0 z-40">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.12em] mb-1">
                {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || 'utilisateur'}
                </h1>
                <span className="text-xl">👋</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="lg:hidden border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 w-10 h-10"
              >
                <LogOut className="w-4.5 h-4.5" />
              </Button>
              <AchievementsDropdown />
              <button
                type="button"
                onClick={openGuide}
                aria-label={t('gettingStarted.label' as any)}
                className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-white"
              >
                <BookOpen className="h-4 w-4 text-[#16c47f]" />
                <span className="hidden sm:inline">{t('gettingStarted.label' as any)}</span>
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16c47f] rounded-full transition-transform hover:scale-105"
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover ring-2 ring-[#16c47f]/30 shadow-sm cursor-pointer"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-[#16c47f] to-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-bold ring-2 ring-[#16c47f]/20 shadow-sm cursor-pointer">
                    {user?.name?.charAt(0).toUpperCase() ||
                      user?.email?.charAt(0).toUpperCase() ||
                      'U'}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Global Filters Bar */}
          <div className="px-6 pb-3 flex flex-wrap items-center gap-3 border-t border-gray-50 dark:border-gray-700/30 pt-3">
            {/* Month Navigation */}
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1.5 bg-gray-100/80 dark:bg-gray-700/60 rounded-xl p-1.5">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white hover:shadow-sm transition-all"
                  aria-label={t('dashboard.filter.previousMonth' as any)}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span
                  className={`px-4 py-2 text-sm font-semibold rounded-lg min-w-[140px] text-center capitalize ${
                    isCurrentMonth
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {selectedMonthLabel}
                </span>
                <button
                  onClick={() => navigateMonth(1)}
                  disabled={isCurrentMonth}
                  className={`p-2 rounded-lg transition-all ${
                    isCurrentMonth
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-white hover:shadow-sm'
                  }`}
                  aria-label={t('dashboard.filter.nextMonth' as any)}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {!isCurrentMonth && (
                <button
                  onClick={goToCurrentMonth}
                  className="h-10 px-3.5 flex items-center gap-2 rounded-xl bg-[#16c47f]/10 hover:bg-[#16c47f]/20 border border-[#16c47f]/20 hover:border-[#16c47f]/40 text-[#16c47f] text-xs font-semibold transition-all"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#16c47f] animate-pulse" />
                  {language === 'fr' ? 'Ce mois' : 'This month'}
                </button>
              )}
            </div>

            <div className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5" />

            {/* Account Filter */}
            <div className="relative">
              <button
                type="button"
                data-testid="dashboard-account-filter-trigger"
                onClick={() => setIsAccountFilterOpen((open) => !open)}
                aria-expanded={isAccountFilterOpen}
                className="min-w-[280px] max-w-[420px] h-12 flex items-center justify-between gap-3 px-4 bg-gray-100/80 dark:bg-gray-700/60 border border-transparent hover:border-gray-200/80 dark:hover:border-gray-600/80 rounded-xl text-left transition-all"
              >
                <div className="min-w-0 flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-3.5 h-3.5 text-[#16c47f]" />
                  </div>
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em] flex-shrink-0">
                      {t('dashboard.accounts')}
                    </span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {accountFilterLabel}
                    </p>
                  </div>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                    isAccountFilterOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isAccountFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setIsAccountFilterOpen(false)}
                  />
                  <div className="absolute left-0 top-full mt-2 z-40 w-[420px] max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/70 dark:bg-gray-900/20">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em]">
                            {t('dashboard.accounts')}
                          </p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {isAllAccountsSelected
                              ? t('dashboard.filter.allAccounts' as any)
                              : `${filteredAccounts.length} ${t('dashboard.accounts')}`}
                          </p>
                        </div>
                      </div>

                      {!isAllAccountsSelected && filteredAccounts.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {filteredAccounts.map((account) => (
                            <button
                              key={account.id}
                              type="button"
                              onClick={() => removeAccountSelection(account.id)}
                              className="max-w-full inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:border-gray-200 dark:hover:border-gray-600 transition-colors"
                            >
                              <span className="truncate max-w-[220px]">{account.name}</span>
                              <X className="w-3 h-3 text-gray-400" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="max-h-[26rem] overflow-y-auto p-2">
                      <button
                        type="button"
                        data-testid="dashboard-account-option-all"
                        onClick={clearAccountFilter}
                        className={`mb-2 w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                          isAllAccountsSelected
                            ? 'bg-[#16c47f]/10 dark:bg-[#16c47f]/15'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div
                          className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                            isAllAccountsSelected
                              ? 'bg-[#16c47f] border-[#16c47f] text-white'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                          }`}
                        >
                          {isAllAccountsSelected && <Check className="w-3 h-3" />}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                            {t('dashboard.filter.allAccounts' as any)}
                          </p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                            {visibleAccounts.length} {t('dashboard.accounts')}
                          </p>
                        </div>
                      </button>

                      {groupedFilterAccounts.map((group) => (
                        <div key={group.key} className="mb-2 last:mb-0">
                          <div className="px-2 pt-2 pb-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.08em]">
                            {group.label}
                          </div>

                          <div className="space-y-1">
                            {group.accounts.map((account) => {
                              const isSelected = selectedAccountIds.includes(account.id);

                              return (
                                <button
                                  key={account.id}
                                  type="button"
                                  data-testid={`dashboard-account-option-${account.id}`}
                                  onClick={() => toggleAccountSelection(account.id)}
                                  className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                                    isSelected
                                      ? 'bg-[#16c47f]/10 dark:bg-[#16c47f]/15'
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                  }`}
                                >
                                  <div
                                    className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                                      isSelected
                                        ? 'bg-[#16c47f] border-[#16c47f] text-white'
                                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                                    }`}
                                  >
                                    {isSelected && <Check className="w-3 h-3" />}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white break-words leading-tight">
                                      {account.name}
                                    </p>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 break-words">
                                      {formatAmountInCurrency(
                                        account.type === 'liability'
                                          ? -Math.abs(account.balance)
                                          : account.balance,
                                        account.currency
                                      )}
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Section 1: Patrimoine Total — Collapsible */}
              <section className="rounded-2xl border border-emerald-100/80 bg-gradient-to-br from-emerald-100/90 via-emerald-50/50 to-white overflow-hidden shadow-sm shadow-emerald-100/40 dark:border-emerald-900/40 dark:from-emerald-950/25 dark:via-gray-800/60 dark:to-gray-800/50">
                {/* Accordion Header */}
                <button
                  onClick={togglePatrimoine}
                  className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#16c47f] to-emerald-600 flex items-center justify-center shadow-sm shadow-emerald-500/20 motion-safe:animate-[pulse_4s_ease-in-out_infinite]">
                      <Wallet className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">
                        {t('dashboard.netWorth.title')}
                      </h2>
                      {!patrimoineExpanded && (
                        <span className="text-lg font-black text-gray-900 dark:text-white tabular-nums">
                          {formatAmount(filteredNetWorth)}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${patrimoineExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${patrimoineExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-5 pb-5 space-y-4">
                    {convertedNetWorth?.rates_updated_at && isAllAccountsSelected && (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">
                        {language === 'fr'
                          ? `Conversion basée sur les taux du ${new Date(convertedNetWorth.rates_updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${new Date(convertedNetWorth.rates_updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                          : `Conversion based on rates from ${new Date(convertedNetWorth.rates_updated_at).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit' })} at ${new Date(convertedNetWorth.rates_updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                      </p>
                    )}
                    <NetWorthBlock
                      netWorth={filteredNetWorth}
                      netWorthHistory={netWorthHistory}
                      selectedPeriod={selectedPeriod}
                      onPeriodChange={setSelectedPeriod}
                      hideHeader
                    />

                    {/* Sub-blocks: Account Cards and Distribution */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2">
                        <AccountCards
                          accounts={filteredAccounts}
                          sparklineData={sparklineData}
                          isLoading={sparklineLoading}
                        />
                      </div>
                      <div className="lg:col-span-1">
                        <PatrimoineDistribution accounts={filteredAccounts} viewBy="type" />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 2: Budget Mensuel — Collapsible */}
              <section className="rounded-2xl border border-blue-100/80 bg-gradient-to-br from-blue-100/90 via-blue-50/50 to-white overflow-hidden shadow-sm shadow-blue-100/40 dark:border-blue-900/40 dark:from-blue-950/25 dark:via-gray-800/60 dark:to-gray-800/50">
                {/* Accordion Header */}
                <button
                  onClick={toggleBudget}
                  className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm shadow-blue-500/20 motion-safe:animate-[pulse_4s_ease-in-out_infinite]">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">
                        {t('dashboard.budget.monthlyTitle' as any)}
                      </h2>
                      {!budgetExpanded && (
                        <span className="text-lg font-black text-gray-900 dark:text-white tabular-nums">
                          {formatAmount(filteredIncome - filteredExpense)}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${budgetExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Accordion Content */}
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${budgetExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="px-5 pb-5 space-y-4">
                    <BudgetBlock
                      income={filteredIncome}
                      expenses={filteredExpense}
                      previousIncome={comparisonIncome}
                      previousExpenses={comparisonExpense}
                    />
                  </div>
                </div>
              </section>

              {/* Section 3: Top Categories */}
              <section>
                <TopCategories transactions={filteredTransactions} />
              </section>

              {/* Section 4: Cash Flow */}
              <section>
                <CashFlowChart transactions={transactions} />
              </section>

              {/* Section 5 & 6 & 7: Transactions + Upcoming Bills + Goals */}
              <section className="grid grid-cols-12 gap-6">
                {/* Left Column: Recent Transactions */}
                <div className="col-span-12 lg:col-span-7">
                  <RecentTransactions transactions={transactions} />
                </div>

                {/* Right Column: Upcoming Bills + Goals */}
                <div className="col-span-12 lg:col-span-5 space-y-6">
                  <UpcomingBills
                    upcomingItems={upcomingRecurring}
                    onAddRecurring={() => setIsRecurringModalOpen(true)}
                    isLoading={recurringLoading}
                  />
                  <GoalsPreview goals={goalsWithProgress} />
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Modals */}
        {showWelcome && (
          <WelcomeModal
            isOpen={showWelcome}
            userName={welcomeName}
            onClose={() => setShowWelcome(false)}
          />
        )}

        <TransferModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          onSuccess={async () => {
            await loadTransactions();
            await loadAccounts();
          }}
        />

        <RecurringTransactionModal
          isOpen={isRecurringModalOpen}
          onClose={() => {
            setIsRecurringModalOpen(false);
            setEditingRecurring(null);
            loadRecurringTransactions();
          }}
          recurring={editingRecurring}
          onSave={handleSaveRecurring}
        />
      </main>
    </div>
  );
}
